import { Injectable } from '@angular/core';
import { HttpClient, HttpParams } from '@angular/common/http';
import { Observable } from 'rxjs';
import { environment } from 'src/environments/environment';
import { ApiHttpService } from 'src/app/_services/api-http.service';

const { API_URLBLAZE } = environment;
export interface RemittanceEarningFilter {
  txnId?: string;
  walletId?: string;
  dateFrom?: string;
  dateTo?: string;
  sendCurrency?: string;
  receiveCurrency?: string;
  currency?: string;
  serviceProviderCode?: string;
  customerMobile?: string;
  receiveCountry?: string;
  page_size?: number | string; 
  page_no?: number;
  productCode?: string;
  sort_dir?: string;
  sort_column?: string;
  txnStatus?: string;
}

export interface RemittanceEarningReportData {
  txnId: string;
  walletId: string;
  trxnInitiated: string;
  trxnUpdated: string;
  sendCountry: string;
  sendCurrency: string;
  senderName: string;
  serviceProviderCode: string;
  receiveCountry: string;
  receiveCurrency: string;
  receiverName: string;
  receiverBankName: string;
  receiverAcNumber: string;
  deliveryOption: string;
  sendAmount: number;
  offeredFxRate: number;
  baseFxRate: number;
  fxEarningAED: number;
  fee: number;
  vatOnFee: number;
  netFee: number;
  spFee: number;
  feeEarning: number;
  totalEarning: number;
  discount: number;
  totalAmount: number;
  receiveAmount: number;
  remitterCompanyName: string;
  occupation: string;
  customerType: string;
  idTypeDetails: string;
  additionalDocuments: string;
  purpose: string;
  relationship: string;
  sourceOfFunds: string;
}

@Injectable({
  providedIn: 'root'
})
export class RemittanceEarningReportService {

  constructor(
    private apiHttpService: ApiHttpService,
    private http: HttpClient
  ) { 
  }

  getRemittanceEarningReport(filter: RemittanceEarningFilter): Observable<any> {  
    const params = this.buildQueryParams(filter);
    const url = `${API_URLBLAZE}/transaction/transaction-report/remittance-earning`;
    return this.apiHttpService.get(url, { params });
  }

  
  exportRemittanceEarningReport(filter: RemittanceEarningFilter): Observable<Blob> {
    const params = this.buildQueryParams(filter);
    return this.http.get(`${API_URLBLAZE}/transaction/transaction-report/remittance-earning/export`, {
      params,
      responseType: 'blob'
    });
  }


  getRemittanceEarningReportBackgroundExport(filter: RemittanceEarningFilter): Observable<any> {
    const params = this.buildQueryParams(filter);
    const url = `${API_URLBLAZE}/transaction/transaction-report/remittance-earning/export`;
    return this.apiHttpService.get(url, { params });
  }

  getServiceProviderCodes(productCode: string = 'CROSS_BORDER_TRANSFER'): Observable<any> {
    const url = `${API_URLBLAZE}/transaction/service-provider/distinct/serviceProviderCodes/product/${productCode}`;
    return this.apiHttpService.get(url);
  }

  
  private buildQueryParams(filter: RemittanceEarningFilter): HttpParams {
    let params = new HttpParams();
    
    if (filter.page_size) {
      if (typeof filter.page_size === 'string' && filter.page_size.toUpperCase() === 'ALL') {
        params = params.set('page_size', 'ALL');
      } else {
        params = params.set('page_size', filter.page_size.toString());
      }
    }
    if (filter.page_no !== undefined) {
      params = params.set('page_no', filter.page_no.toString());
    }
    
    if (filter.dateFrom && filter.dateTo && filter.dateFrom.trim() !== '' && filter.dateTo.trim() !== '') {
      const dateFromFormatted = `${filter.dateFrom} 00:00:00`;
      const dateToFormatted = `${filter.dateTo} 23:59:59`;
      const createDateValue = `gte=${dateFromFormatted}$lte=${dateToFormatted}`;
      params = params.set('createDate', createDateValue);
    } else if (filter.dateFrom && filter.dateFrom.trim() !== '') {
      const dateFromFormatted = `${filter.dateFrom} 00:00:00`;
      const dateToFormatted = `${filter.dateFrom} 23:59:59`;
      const createDateValue = `gte=${dateFromFormatted}$lte=${dateToFormatted}`;
      params = params.set('createDate', createDateValue);
    } else if (filter.dateTo && filter.dateTo.trim() !== '') {
      const dateToFormatted = `${filter.dateTo} 00:00:00`;
      const dateToFormattedEnd = `${filter.dateTo} 23:59:59`;
      const createDateValue = `gte=${dateToFormatted}$lte=${dateToFormattedEnd}`;
      params = params.set('createDate', createDateValue);
    }
    
    params = params.set('sort_dir', filter.sort_dir || 'DESC');
    params = params.set('sort_column', filter.sort_column || 'createDate');
    
    params = params.set('productCode', filter.productCode || 'CROSS_BORDER_TRANSFER');
    params = params.set('status', filter.txnStatus || 'SUCCESS');    
    
    if (filter.txnId && filter.txnId.trim() !== '') {
      params = params.set('txnId', filter.txnId.trim().toUpperCase()  );
    }
    if (filter.walletId && filter.walletId.trim() !== '') {
      params = params.set('walletId', filter.walletId.trim().toUpperCase()  );
    }
    if (filter.customerMobile && filter.customerMobile.trim() !== '') {
      params = params.set('userMobile', filter.customerMobile.trim());
    }
    if (filter.currency) {
      params = params.set('currency', filter.currency);
    } else if (filter.receiveCurrency && filter.receiveCurrency.trim() !== '') {
      params = params.set('currency', filter.receiveCurrency);
    } else if (filter.sendCurrency && filter.sendCurrency.trim() !== '') {
      params = params.set('currency', filter.sendCurrency);
    }
    if (filter.serviceProviderCode && filter.serviceProviderCode.trim() !== '') {
      params = params.set('serviceProviderCode', filter.serviceProviderCode.trim());
    }
    if (filter.receiveCountry && filter.receiveCountry.trim() !== '') {
      params = params.set('receiveCountry', filter.receiveCountry.trim());
    }

    return params;
  }


  mapApiResponseToComponentData(apiRecord: any): RemittanceEarningReportData {

    const receiverName = apiRecord.beneficiaryName || '';
    const receiverBankName = apiRecord.beneficiaryBankName || '';
    const receiverAcNumber = apiRecord.beneficiaryAccountNo || '';
    const deliveryOption = apiRecord.beneficiaryBankType || '';
    const receiverAcDetails = apiRecord.beneficiaryBankDetails || '';

    return {
      txnId: apiRecord.txnId || '',
      walletId: apiRecord.walletId || '',
      trxnInitiated: apiRecord.txnInitiatedDate || '',
      trxnUpdated: apiRecord.txnUpdatedDate || '',
      sendCountry: apiRecord.sendingCountry || '',
      sendCurrency: apiRecord.sendingCurrency || '',
      senderName: apiRecord.senderName || '',
      serviceProviderCode: apiRecord.serviceProviderCode || '',
      receiveCountry: apiRecord.receiverCountry || '',
      receiveCurrency: apiRecord.receiverCurrency || '',
      receiverName: receiverName,
      receiverBankName: receiverBankName,
      receiverAcNumber: receiverAcDetails,
      deliveryOption: deliveryOption,
      sendAmount: apiRecord.sendingAmount || 0,
      offeredFxRate: apiRecord.exchangeRate || 0,
      baseFxRate: apiRecord.spOfferedExchangeRate || 0,
      fxEarningAED: apiRecord.fxEarning || 0,
      fee: apiRecord.serviceCharges || 0,
      vatOnFee: apiRecord.vat || 0,
      netFee: apiRecord.netFee || 0,
      spFee: apiRecord.spFee || 0,
      feeEarning: apiRecord.feeEarning || 0,
      totalEarning: apiRecord.totalEarning || 0,
      discount: apiRecord.discount || 0,
      totalAmount: apiRecord.totalAmount || 0,
      receiveAmount: apiRecord.receivingAmount || 0,
      remitterCompanyName: apiRecord.remitterCompanyName || '',
      occupation: apiRecord.occupation || '',
      customerType: apiRecord.customerType || '',
      idTypeDetails: apiRecord.senderIdNumber || '', 
      additionalDocuments: apiRecord.additionalDocuments || 'NA',
      purpose: apiRecord.purpose || '',
      relationship: apiRecord.relationship || '',
      sourceOfFunds: apiRecord.sourceOfFunds || ''
    };
  }

  calculateAllEarnings(record: any): RemittanceEarningReportData {
    return {
      ...record,
    };
  }
}

